// src/services/BranchAssignmentService.ts
import type { AncestorGraph } from '../domain/AncestorGraph';
import type { LineagePath } from '../domain/LineagePath';
import type { Person } from '../domain/Person';
import { createLogger } from '../utils/logger';

const logger = createLogger('BranchAssignmentService');

/**
 * Okabe-Ito 8-color qualitative palette (colorblind-safe).
 * Exported for use by rendering layers (P-07/P-08).
 */
export const OKABE_ITO: readonly string[] = [
  '#E69F00', // orange
  '#56B4E9', // sky blue
  '#009E73', // bluish green
  '#F0E442', // yellow
  '#0072B2', // blue
  '#D55E00', // vermilion
  '#CC79A7', // reddish purple
  '#000000', // black
];

/** Defines one of the 8 great-grandparent branch slots. */
interface BranchSlot {
  branchId: string;
  /** Path of fatherId/motherId keys from root to this slot's generation-3 position. */
  parentKeys: Array<'fatherId' | 'motherId'>;
}

const BRANCH_SLOTS: readonly BranchSlot[] = [
  { branchId: 'paternal-paternal-paternal', parentKeys: ['fatherId', 'fatherId', 'fatherId'] },
  { branchId: 'paternal-paternal-maternal', parentKeys: ['fatherId', 'fatherId', 'motherId'] },
  { branchId: 'paternal-maternal-paternal', parentKeys: ['fatherId', 'motherId', 'fatherId'] },
  { branchId: 'paternal-maternal-maternal', parentKeys: ['fatherId', 'motherId', 'motherId'] },
  { branchId: 'maternal-paternal-paternal', parentKeys: ['motherId', 'fatherId', 'fatherId'] },
  { branchId: 'maternal-paternal-maternal', parentKeys: ['motherId', 'fatherId', 'motherId'] },
  { branchId: 'maternal-maternal-paternal', parentKeys: ['motherId', 'motherId', 'fatherId'] },
  { branchId: 'maternal-maternal-maternal', parentKeys: ['motherId', 'motherId', 'motherId'] },
];

export interface BranchAssignmentResult {
  assignments: Map<string, string>;
  paths: LineagePath[];
  unassigned: string[];
}

export class BranchAssignmentService {
  assignBranches(graph: AncestorGraph): BranchAssignmentResult {
    logger.info(`assignBranches started — ${graph.persons.size} persons in graph`);

    const assignments = new Map<string, string>();

    // Step 1: Resolve each slot's great-grandparent by walking the defining path.
    const slotPersonIds: Array<string | undefined> = BRANCH_SLOTS.map((slot) => {
      let current: Person | undefined = graph.persons.get(graph.rootId);
      for (const key of slot.parentKeys) {
        if (current === undefined) {
          return undefined;
        }
        const nextId = current[key];
        current = nextId !== undefined ? graph.persons.get(nextId) : undefined;
      }
      return current?.id;
    });

    // Step 2: Per-branch DFS from each populated great-grandparent.
    for (let i = 0; i < BRANCH_SLOTS.length; i++) {
      const ggpId = slotPersonIds[i];
      const slot = BRANCH_SLOTS[i];
      if (ggpId === undefined || slot === undefined) {
        continue;
      }
      this.dfsAssign(graph, ggpId, slot.branchId, assignments);
    }

    // Step 3: Assign intermediates (gen 0–2) to their first descendant branch.
    this.assignIntermediates(graph, slotPersonIds, assignments);

    // Step 4: Build LineagePath[] for each populated slot.
    const paths: LineagePath[] = [];
    let colorIndex = 0;
    for (let i = 0; i < BRANCH_SLOTS.length; i++) {
      const slot = BRANCH_SLOTS[i];
      if (slotPersonIds[i] === undefined || slot === undefined) {
        continue;
      }
      const personIds = this.buildPathPersonIds(graph, slot);
      paths.push({
        branchId: slot.branchId,
        branchColor: OKABE_ITO[colorIndex] ?? '#999999',
        personIds,
      });
      colorIndex++;
    }

    // Step 5: Collect unassigned.
    const unassigned: string[] = [];
    for (const id of graph.persons.keys()) {
      if (!assignments.has(id)) {
        unassigned.push(id);
      }
    }

    logger.info(
      `assignBranches complete — ${paths.length} branches populated, ${assignments.size} persons assigned, ${unassigned.length} unassigned`,
    );

    return { assignments, paths, unassigned };
  }

  /** DFS from a person, assigning all reachable ancestors to the given branch. */
  private dfsAssign(
    graph: AncestorGraph,
    personId: string,
    branchId: string,
    assignments: Map<string, string>,
  ): void {
    if (assignments.has(personId)) {
      return; // first-assigned-wins
    }
    const person = graph.persons.get(personId);
    if (person === undefined) {
      return;
    }
    assignments.set(personId, branchId);

    // Father first for deterministic walk order.
    if (person.fatherId !== undefined) {
      this.dfsAssign(graph, person.fatherId, branchId, assignments);
    }
    if (person.motherId !== undefined) {
      this.dfsAssign(graph, person.motherId, branchId, assignments);
    }
  }

  /** Assign gen 0–2 persons to their first descendant branch. */
  private assignIntermediates(
    graph: AncestorGraph,
    slotPersonIds: Array<string | undefined>,
    assignments: Map<string, string>,
  ): void {
    const root = graph.persons.get(graph.rootId);
    if (root === undefined) {
      return;
    }

    // Gen-2 grandparents: each covers 2 slots.
    // GP index 0 (father's father) → slots 0,1
    // GP index 1 (father's mother) → slots 2,3
    // GP index 2 (mother's father) → slots 4,5
    // GP index 3 (mother's mother) → slots 6,7
    const gen1Ids: Array<string | undefined> = [root.fatherId, root.motherId];
    const gen2Ids: Array<string | undefined> = [];
    for (const parentId of gen1Ids) {
      const parent = parentId !== undefined ? graph.persons.get(parentId) : undefined;
      gen2Ids.push(parent?.fatherId, parent?.motherId);
    }

    // Assign each gen-2 grandparent to first populated slot in their pair.
    for (let gpIdx = 0; gpIdx < 4; gpIdx++) {
      const gpId = gen2Ids[gpIdx];
      if (gpId === undefined || !graph.persons.has(gpId) || assignments.has(gpId)) {
        continue;
      }
      const slotBase = gpIdx * 2;
      for (let s = slotBase; s < slotBase + 2; s++) {
        const slot = BRANCH_SLOTS[s];
        if (slotPersonIds[s] !== undefined && slot !== undefined) {
          assignments.set(gpId, slot.branchId);
          break;
        }
      }
    }

    // Assign each gen-1 parent to first populated slot in their 4-slot range.
    for (let pIdx = 0; pIdx < 2; pIdx++) {
      const pId = gen1Ids[pIdx];
      if (pId === undefined || !graph.persons.has(pId) || assignments.has(pId)) {
        continue;
      }
      const slotBase = pIdx * 4;
      for (let s = slotBase; s < slotBase + 4; s++) {
        const slot = BRANCH_SLOTS[s];
        if (slotPersonIds[s] !== undefined && slot !== undefined) {
          assignments.set(pId, slot.branchId);
          break;
        }
      }
    }

    // Assign root to first populated slot overall.
    if (!assignments.has(graph.rootId)) {
      for (let s = 0; s < BRANCH_SLOTS.length; s++) {
        const slot = BRANCH_SLOTS[s];
        if (slotPersonIds[s] !== undefined && slot !== undefined) {
          assignments.set(graph.rootId, slot.branchId);
          break;
        }
      }
    }
  }

  /**
   * Build personIds for a LineagePath: root → gen-1 → gen-2 → great-grandparent → EKA.
   * Follows the slot's defining path for gen 0–2, then fatherId at every level beyond.
   */
  private buildPathPersonIds(graph: AncestorGraph, slot: BranchSlot): string[] {
    const ids: string[] = [];
    let current: Person | undefined = graph.persons.get(graph.rootId);

    // Walk root → great-grandparent via the slot's defining path.
    if (current !== undefined) {
      ids.push(current.id);
    }
    for (const key of slot.parentKeys) {
      if (current === undefined) {
        break;
      }
      const nextId = current[key];
      current = nextId !== undefined ? graph.persons.get(nextId) : undefined;
      if (current !== undefined) {
        ids.push(current.id);
      }
    }

    // Beyond the great-grandparent: follow fatherId at every level to EKA.
    if (current !== undefined) {
      let ancestor =
        current.fatherId !== undefined ? graph.persons.get(current.fatherId) : undefined;
      while (ancestor !== undefined) {
        ids.push(ancestor.id);
        ancestor =
          ancestor.fatherId !== undefined ? graph.persons.get(ancestor.fatherId) : undefined;
      }
    }

    return ids;
  }
}
